import { FC, Fragment, useState, useEffect } from "react";

import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
} from "@mui/material";

import ExpandLess from "@mui/icons-material/ExpandLess";

import ExpandMore from "@mui/icons-material/ExpandMore";

import { useHistory } from "react-router-dom";

import { App } from "../../typings/App";

import { useAuth } from "oidc-react";

import { MenuItem } from "../Menu/MenuItem";

import { Submenu } from "../../typings/Submenu";

import { Item } from "../../typings/Item";

import { useUIMode } from "../../wms/src/context/UIContext";

// Importar todas las imágenes de logos usando import.meta.glob de Vite
const logoModules = import.meta.glob('../../assets/appsLogos/*.png', { eager: true, import: 'default' }) as Record<string, string>;

interface Props {
  app: App;
  onCloseDrawer?: () => void;
  selectedItem?: string | null;
  onSelectItem?: (id: string) => void;
  onSelectName?: (name: string) => void;
}

export const AppItem: FC<Props> = ({
  app,
  onCloseDrawer,
  selectedItem,
  onSelectItem,
  onSelectName,
}) => {
  const auth = useAuth();

  const history = useHistory();

  const [isOpen, setIsOpen] = useState(false);

  // 3. Obtenemos el estado desde nuestro Contexto Seguro
  const { isShortMode } = useUIMode();

  // Función helper para validar visibilidad basada en el modo actual
  const isVisibleInCurrentMode = (visibility?: 'mobile' | 'desktop' | 'both') => {
    const mode = visibility || 'desktop'; // Por defecto es desktop

    if (mode === 'both') return true;
    if (mode === 'mobile') return isShortMode;
    if (mode === 'desktop') return !isShortMode;
    return true;
  };

  // Obtener la imagen del logo usando import.meta.glob
  const imagePath = `../../assets/appsLogos/${app.iconUrl}.png`;
  const image = logoModules[imagePath] || '';

  const handleItemClick = () => {
    setIsOpen(!isOpen);

    if (!app.submenus || app.submenus.length === 0) {
      history.push(app.url);
    }
  };

  const handleSubitemClick = (item: Item | Submenu) => {
    history.push(`${app.url}/${item.url}`);
    if (onSelectItem) {
      onSelectItem(item.url);
    }
    if (onSelectName) {
      const displayName = 'headerName' in item && (item as any).headerName ? (item as any).headerName : item.name;
      onSelectName(displayName);
    }
    if (onCloseDrawer) {
      onCloseDrawer();
    }
  };

  const userRoles: string[] =
    (auth.userData?.profile["role"] as string[]) || [];

  // Filtrado simplificado según roles padre e hijos
  const filteredSubmenus: Submenu[] = app.submenus?.reduce<Submenu[]>(
    (acc, submenu) => {
      const hasParentRole = submenu.roles?.some(r => userRoles.includes(r));
      const children = submenu.items ?? [];

      // Si no tiene hijos y tiene rol padre, incluir
      if (children.length === 0) {
        // Leemos la propiedad del config para mostrar o no menus
        const isVisibleInCurrentView = isVisibleInCurrentMode(submenu.visibility);

        if (hasParentRole && isVisibleInCurrentView) {
          acc.push(submenu);
        }
        return acc;
      }

      // Filtrar sólo hijos permitidos
      let allowedChildren = hasParentRole
        ? children
        : children.filter((item) =>
            item.roles?.some((r) => userRoles.includes(r)),
          );

      // Siempre filtrar por visibility, independientemente del modo
      allowedChildren = allowedChildren.filter((item) => isVisibleInCurrentMode((item as any).visibility));

      if (isShortMode) {
        // En modo RF, SOLO mostramos el menú padre si quedó algún hijo válido tras el filtro
        if (allowedChildren.length > 0) {
          acc.push({ ...submenu, items: allowedChildren });
        }
      } else {
        // En modo completo, siempre usar hijos filtrados
        if (allowedChildren.length > 0) {
          acc.push({ ...submenu, items: allowedChildren });
        }
      }

      return acc;
    }, []) ?? [];

  const handleAppItemClick = () => {
    handleItemClick();
    if (onSelectItem) {
      onSelectItem(app.url); // Usa un identificador único del app
    }
  };

  return (
    <Fragment>
      <List component="div" disablePadding sx={{ width: '100%' }}>
        {filteredSubmenus.map((submenu, idx) => (
          <MenuItem
            key={idx}
            submenu={submenu}
            handleSubitemClick={handleSubitemClick}
            selectedItem={selectedItem}
            onSelectItem={onSelectItem}
          />
        ))}
      </List>
    </Fragment>
  );
};
